import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  Check,
  GraduationCap,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { useUniversityOptions } from "@/features/universityAndCourse/api/useUniversities";
import { useCourseOptions } from "@/features/universityAndCourse/api/useCourses";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { Dialog, DialogFooter } from "@/components/ui/library/dialog";

interface ChangeUniversityModalProps {
  open: boolean;
  onClose: () => void;
  dealId: number;
  dealName: string;
  /** Currently-attached university name, used only to flag the active row. */
  currentUniversityName?: string;
}

/**
 * University changer for a deal's Course card.
 *
 * Lists ONLY universities under the deal's country (server scopes via
 * `forChildId` — the deal's country is a CF on the child) and never touches the
 * country itself. Because a deal links to a Course (and the University is that
 * course's parent), picking a university re-points the deal to one of that
 * university's courses: auto-linked when it has a single course, otherwise the
 * operator picks. The country/preferences stay as-is — we send only
 * `moduleScopes`, so `ChildUpdate` leaves custom fields untouched. The server
 * also re-validates the university↔country match as a backstop.
 */
export default function ChangeUniversityModal({
  open,
  onClose,
  dealId,
  dealName,
  currentUniversityName,
}: ChangeUniversityModalProps) {
  const { universities, isLoading } = useUniversityOptions(
    { forChildId: String(dealId) },
    { enabled: open },
  );

  const [search, setSearch] = useState("");
  const [selectedUni, setSelectedUni] = useState<any>(null);
  const [selectedCourse, setSelectedCourse] = useState<any>(null);

  // Courses of the chosen university (server-filtered). Fetched only after a
  // university is picked, so we don't walk every catalog on open.
  const { courses, isLoading: coursesLoading } = useCourseOptions({
    enabled: Boolean(open && selectedUni?.id),
    params: selectedUni?.id ? { universityId: String(selectedUni.id) } : undefined,
  });

  // Single-course universities auto-resolve to that course (a true one-click
  // "university change"); multi-course ones require an explicit course pick.
  useEffect(() => {
    if (!selectedUni) return;
    if (coursesLoading) return;
    if (courses.length === 1) setSelectedCourse(courses[0]);
    else setSelectedCourse(null);
  }, [selectedUni, courses, coursesLoading]);

  const filteredUnis = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return universities;
    return universities.filter((u: any) =>
      String(u?.name ?? "").toLowerCase().includes(q),
    );
  }, [universities, search]);

  const { updateChild } = useChildMutations();

  const resetState = () => {
    setSearch("");
    setSelectedUni(null);
    setSelectedCourse(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedCourse?.id) return;
    updateChild.mutate(
      {
        id: dealId,
        // Only the course link changes. No `financials` / `customInputScopes`,
        // so the deal value, country, and other preferences are left untouched.
        data: {
          moduleScopes: [
            { nicheModelName: "course", nicheItemId: selectedCourse.id },
          ],
        },
      },
      {
        onSuccess: () => {
          // updateChild's hook refreshes deals / current deal / lead / board.
          toast.success("University updated");
          resetState();
          onClose();
        },
      },
    );
  };

  const needsCoursePick =
    selectedUni && !coursesLoading && courses.length > 1 && !selectedCourse;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="w-[min(94vw,560px)] rounded-[24px] p-0 gap-0 max-h-[min(86vh,640px)] flex flex-col overflow-hidden">
        <DialogHeader className="px-4 pt-[11px] pb-[11px] border-b border-[#e6e8e7]">
          <p className="text-[18px] font-medium text-black tracking-[-0.72px] leading-normal">
            Change University
          </p>
          <DialogDescription className="text-[#505e59] text-[13px] tracking-[-0.52px] leading-[1.35] mt-0.5">
            {dealName} · showing universities in this deal's country
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col">
          {/* Search */}
          <div className="px-4 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#9ba3a0]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search universities"
                className="w-full h-9 pl-8 pr-3 text-[13px] rounded-lg border border-[#e6e8e7] bg-[#f7f9f8] text-[#1e302a] placeholder:text-[#b3b8b5] focus:outline-none focus:border-[var(--color-success)] focus:ring-1 focus:ring-[var(--color-success)]/20 transition-colors"
              />
            </div>
          </div>

          {/* University list */}
          <div className="px-4 pb-2 flex-1 min-h-0 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="size-5 animate-spin text-[#9ba3a0]" />
              </div>
            ) : filteredUnis.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Building2 className="size-8 text-[#c5c9c7] mb-2" />
                <p className="text-[13px] text-[#8a918d]">
                  {search.trim()
                    ? "No universities match your search"
                    : "No universities found for this deal's country"}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {filteredUnis.map((uni: any) => {
                  const isSelected = selectedUni?.id === uni.id;
                  const isCurrent =
                    !!currentUniversityName &&
                    String(uni?.name ?? "").trim() ===
                      currentUniversityName.trim();
                  const courseCount =
                    uni?.coursesCount ?? uni?.courses?.length ?? 0;

                  return (
                    <div
                      key={uni.id}
                      className="rounded-xl border border-[#e6e8e7] overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedUni(isSelected ? null : uni)
                        }
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors ${
                          isSelected
                            ? "bg-[#f0faf5]"
                            : "hover:bg-[#f7f9f8]"
                        }`}
                      >
                        <div
                          className={`size-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                            isSelected
                              ? "border-[var(--color-success)] bg-[var(--color-success)]"
                              : "border-[#c5c9c7]"
                          }`}
                        >
                          {isSelected && (
                            <Check className="size-2.5 text-white" strokeWidth={3} />
                          )}
                        </div>
                        <span
                          className={`text-[13px] truncate flex-1 ${
                            isSelected
                              ? "font-semibold text-[var(--color-success)]"
                              : "font-medium text-[#1e302a]"
                          }`}
                        >
                          {uni?.name ?? "Unnamed University"}
                        </span>
                        {isCurrent && (
                          <span className="text-[10px] font-medium text-[#697571] bg-[#eef3f1] rounded-full px-2 py-0.5 shrink-0">
                            Current
                          </span>
                        )}
                        <span className="text-[11px] text-[#9ba3a0] shrink-0">
                          {courseCount} course{courseCount !== 1 ? "s" : ""}
                        </span>
                      </button>

                      {/* Course resolution for the selected university */}
                      {isSelected && (
                        <div className="border-t border-[#f0f1f0] bg-[#fafbfa] px-3.5 py-2.5">
                          {coursesLoading ? (
                            <div className="flex items-center gap-2 text-[12px] text-[#8a918d]">
                              <Loader2 className="size-3.5 animate-spin" />
                              Loading courses…
                            </div>
                          ) : courses.length === 0 ? (
                            <p className="text-[12px] text-(--color-error)">
                              This university has no courses to attach.
                            </p>
                          ) : courses.length === 1 ? (
                            <div className="flex items-center gap-2 text-[12px] text-[#374741]">
                              <GraduationCap className="size-3.5 text-[#697571] shrink-0" />
                              <span className="truncate">{courses[0]?.name}</span>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <p className="text-[11px] text-[#8a918d] mb-0.5">
                                Pick a course under this university
                              </p>
                              {courses.map((c: any) => {
                                const courseSelected = selectedCourse?.id === c.id;
                                return (
                                  <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setSelectedCourse(c)}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
                                      courseSelected
                                        ? "bg-[#f0faf5]"
                                        : "hover:bg-[#f0f1f0]"
                                    }`}
                                  >
                                    <div
                                      className={`size-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                        courseSelected
                                          ? "border-[var(--color-success)] bg-[var(--color-success)]"
                                          : "border-[#c5c9c7]"
                                      }`}
                                    >
                                      {courseSelected && (
                                        <Check
                                          className="size-2 text-white"
                                          strokeWidth={3}
                                        />
                                      )}
                                    </div>
                                    <span
                                      className={`text-[12px] truncate ${
                                        courseSelected
                                          ? "font-semibold text-[var(--color-success)]"
                                          : "text-[#374741]"
                                      }`}
                                    >
                                      {c?.name}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-4 pb-4 pt-2 border-t border-[#e6e8e7] flex sm:justify-between items-center gap-2">
          <div className="text-[12px] text-[#697571] flex-1 min-w-0 truncate">
            {!selectedUni ? (
              <span className="text-[#9ba3a0]">Select a university</span>
            ) : needsCoursePick ? (
              <span className="text-(--color-error)">Pick a course to continue</span>
            ) : selectedCourse ? (
              <span>
                New university:{" "}
                <strong className="text-[#1e302a]">{selectedUni?.name}</strong>
              </span>
            ) : (
              <span className="text-[#9ba3a0]">No course available</span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <DialogClose asChild>
              <Button variant="cancel" disabled={updateChild.isPending}>
                Cancel
              </Button>
            </DialogClose>

            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!selectedCourse || updateChild.isPending}
            >
              {updateChild.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              {updateChild.isPending ? "Updating…" : "Change"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
